import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { SelfDeleteReasonDto } from "@/api/types";
import { useLogout } from "@/authentication/useLogout";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormRadioCard, FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { Modal } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Divider } from "@/design-system/Divider";
import { FormField } from "@/design-system/FormField";
import { Heading } from "@/design-system/Heading";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { useBool } from "@/hooks/useBool";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { useTranslation } from "@/translations";

const DELETE_ACCOUNT_FORM_ID = "delete-account-form";

type DeleteAccountStep = "warning" | "reason" | "success";

interface DeleteAccountFormValues {
  reason?: SelfDeleteReasonDto;
  details: string;
}

export function DeleteAccountSection(): React.ReactNode {
  const [isModalOpen, modalHandler] = useBool(false);

  const { t } = useTranslation();

  return (
    <>
      <Divider />
      <Stack gap="md" align="start">
        <Stack gap="xs">
          <Heading as="h2" level={4}>
            {t("page.self-edit.delete-account.title")}
          </Heading>
          <Text as="p" tone="secondary">
            {t("page.self-edit.delete-account.description")}
          </Text>
        </Stack>
        <Button type="button" variant="filled" tone="error" onClick={modalHandler.setTrue}>
          {t("page.self-edit.delete-account.action.open")}
        </Button>
      </Stack>
      {isModalOpen && <DeleteAccountModal isOpened onOpenChange={modalHandler.set} />}
    </>
  );
}

interface DeleteAccountModalProps {
  isOpened: boolean;
  onOpenChange: (isOpened: boolean) => void;
}

function DeleteAccountModal({ isOpened, onOpenChange }: DeleteAccountModalProps): React.ReactNode {
  const [step, setStep] = useState<DeleteAccountStep>("warning");
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const logout = useLogout();
  const deleteSelfMutation = selfAccountMutations.useDeleteSelf();
  const form = useForm<DeleteAccountFormValues>({
    defaultValues: {
      reason: undefined,
      details: "",
    },
  });
  const reason = useWatch({ control: form.control, name: "reason" });
  const detailsRequired = reason !== "moving";

  function closeModal() {
    if (!deleteSelfMutation.isPending) {
      onOpenChange(false);
    }
  }

  function submitDeletion(values: DeleteAccountFormValues) {
    if (!values.reason) {
      return;
    }

    const details = values.details.trim();

    deleteSelfMutation.mutate(
      {
        reason: values.reason,
        details: details || undefined,
      },
      {
        onError: () => {
          showFlashToast({
            variant: "alert",
            message: t("page.self-edit.delete-account.submit.error"),
          });
        },
        onSuccess: () => setStep("success"),
      },
    );
  }

  if (step === "warning") {
    return (
      <Modal.Root
        data-testid="delete-account-warning-modal"
        title={t("page.self-edit.delete-account.warning.title")}
        description={t("page.self-edit.delete-account.warning.description")}
        isOpened={isOpened}
        onOpenChange={(open) => {
          if (!open) {
            closeModal();
          }
        }}
        showClose={false}
        shouldCloseOnClickOutside={false}
        size="sm"
      >
        <Modal.Body>
          <Stack gap="md">
            <Text as="p">{t("page.self-edit.delete-account.warning.unavailable-intro")}</Text>
            <Box padding={{ x: "md" }}>
              <Text as="div" tone="secondary">
                <ul className="list-disc">
                  <li>{t("page.self-edit.delete-account.warning.unavailable.contact")}</li>
                  <li>{t("page.self-edit.delete-account.warning.unavailable.manuals")}</li>
                  <li>{t("page.self-edit.delete-account.warning.unavailable.messages")}</li>
                  <li>{t("page.self-edit.delete-account.warning.unavailable.tickets")}</li>
                  <li>{t("page.self-edit.delete-account.warning.unavailable.updates")}</li>
                </ul>
              </Text>
            </Box>
            <Notice variant="warning" message={t("page.self-edit.delete-account.warning.open-tickets")} />
          </Stack>
        </Modal.Body>
        <Modal.Actions>
          <Button type="button" variant="outline" onClick={closeModal}>
            {t("common.action.cancel")}
          </Button>
          <Button type="button" variant="filled" tone="error" onClick={() => setStep("reason")}>
            {t("page.self-edit.delete-account.warning.action.continue")}
          </Button>
        </Modal.Actions>
      </Modal.Root>
    );
  }

  if (step === "success") {
    return (
      <Modal.Root
        data-testid="delete-account-success-modal"
        title={t("page.self-edit.delete-account.success.title")}
        description={t("page.self-edit.delete-account.success.description")}
        isOpened={isOpened}
        onOpenChange={() => undefined}
        showClose={false}
        shouldCloseOnClickOutside={false}
        shouldCloseOnEsc={false}
        size="sm"
      >
        <Modal.Actions>
          <Button type="button" onClick={() => void logout()}>
            {t("page.self-edit.delete-account.success.action.logout")}
          </Button>
        </Modal.Actions>
      </Modal.Root>
    );
  }

  return (
    <Modal.Root
      data-testid="delete-account-reason-modal"
      title={t("page.self-edit.delete-account.reason.title")}
      isOpened={isOpened}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      showClose={false}
      shouldCloseOnClickOutside={false}
      shouldCloseOnEsc={!deleteSelfMutation.isPending}
      size="sm"
    >
      <Modal.Body>
        <Form.Root
          id={DELETE_ACCOUNT_FORM_ID}
          formMethods={form}
          onSubmit={submitDeletion}
          noPrompt
          stopSubmitPropagation
          fullWidth
        >
          <Stack gap="md">
            <FormRadioCardGroup layout="vertical" legend={t("page.self-edit.delete-account.reason.title")}>
              <FormRadioCard<DeleteAccountFormValues, "reason">
                name="reason"
                value="appMalfunctioning"
                label={t("page.self-edit.delete-account.reason.app-malfunctioning")}
                description={t("page.self-edit.delete-account.reason.app-malfunctioning.description")}
              />
              <FormRadioCard<DeleteAccountFormValues, "reason">
                name="reason"
                value="moving"
                label={t("page.self-edit.delete-account.reason.moving")}
              />
              <FormRadioCard<DeleteAccountFormValues, "reason">
                name="reason"
                value="other"
                label={t("page.self-edit.delete-account.reason.other")}
              />
            </FormRadioCardGroup>
            <FormField label={t("page.self-edit.delete-account.details.label")} required={detailsRequired}>
              <FormTextArea<DeleteAccountFormValues, "details">
                name="details"
                placeholder={t("page.self-edit.delete-account.details.placeholder")}
                rules={{
                  required: detailsRequired ? t("page.self-edit.delete-account.details.required") : false,
                }}
              />
            </FormField>
          </Stack>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        <Button
          type="button"
          variant="outline"
          onClick={() => setStep("warning")}
          disabled={deleteSelfMutation.isPending}
        >
          {t("common.action.back")}
        </Button>
        <Button
          type="submit"
          form={DELETE_ACCOUNT_FORM_ID}
          variant="filled"
          tone="error"
          disabled={!reason}
          isLoading={deleteSelfMutation.isPending}
        >
          {t("page.self-edit.delete-account.action.submit")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
